import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart,
  TimeScale,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  ChartOptions,
} from "chart.js";
import "chartjs-adapter-moment";
import zoomPlugin from "chartjs-plugin-zoom";
import { Grid, Typography } from "@mui/material";
import { icons } from "constants/icons";
import ModalImage from "./ModalImage";
import { useAppSelector } from "hooks/reduxHooks";
import { selectExamList } from "store/PatientStore/selectors";
import moment from "moment";
import { getRouteLink } from "utils/general";
import { PAGES } from "constants/pages";
import { getGraphTypeColors } from "./commons";
import { ExamItem } from "types/Exam";

export enum LineType {
  Extension,
  Flexion,
}

export const LineTypeContent: Record<LineType, string> = {
  [LineType.Extension]:
    "Extension numbers depict the maximum angle observed on exams for this knee. This will usually be the angle of the knee at full extension, when the patient straightens their leg as much as possible.",
  [LineType.Flexion]:
    "Flexion numbers depict the minimum angle observed on exams for this knee. This will usually be the angle of the knee at when the leg is fully flexed, or bent as much as comfortably possible.",
};

interface LineRegionChartProps {
  ids: string[];
  labels: string[];
  contralateralLabels?: string[];
  data: number[];
  contralateralData: number[];
  isContralateral: boolean;
  opositeChartData: number[];
  graphType: LineType;
}

const LineRegionChart: React.FC<LineRegionChartProps> = ({
  ids,
  labels,
  data,
  opositeChartData,
  contralateralData,
  contralateralLabels,
  isContralateral,
  graphType,
}) => {
  Chart.register(
    TimeScale,
    PointElement,
    LineController,
    LineElement,
    LinearScale,
    zoomPlugin
  );
  const examList = useAppSelector(selectExamList);
  data = data.map((exam) => exam ?? 0);
  contralateralData = contralateralData.map((exam) => exam ?? 0);
  const getChartData = () => {
    const parsedData: any = [];

    Object.keys(labels).forEach((label, index) => {
      const currentItem = { key: labels[index], value: data[index] };
      parsedData.push(currentItem);
    });

    return {
      datasets: [
        {
          label: "Data",
          data: parsedData,
          fill: true,
          ...getGraphTypeColors(graphType, false),
          pointRadius: 6,
          parsing: {
            xAxisKey: "key",
            yAxisKey: "value",
          },
        },
      ],
    };
  };

  const chartData = getChartData();
  const checkIfAllHaveSameDate = () => {
    const parsedData = chartData.datasets[0].data;
    if (parsedData.length === 1) {
      return parsedData[0].key;
    } else {
      let hasSameDate = {
        state: true,
        day: 0,
        month: 0,
        year: 0,
      };

      parsedData.forEach((dataPoint: any, index: number) => {
        const date = new Date(dataPoint.key);

        if (index === 0) {
          hasSameDate = {
            ...hasSameDate,
            day: date.getUTCDate(),
            month: date.getUTCMonth(),
            year: date.getUTCFullYear(),
          };
        } else {
          const haveSameDay = date.getUTCDate() === hasSameDate.day;
          const haveSameMonth = date.getUTCMonth() === hasSameDate.month;
          const haveSameYear = date.getUTCFullYear() === hasSameDate.year;
          const haveSameData = haveSameDay && haveSameMonth && haveSameYear;
          if (!haveSameData) {
            hasSameDate = { ...hasSameDate, state: false };
          }
        }
      });

      if (hasSameDate.state && parsedData[0]) {
        return parsedData[0].key;
      } else {
        return null;
      }
    }
  };
  if (isContralateral && contralateralLabels) {
    chartData.datasets.push({
      label: "Contralateral Data",
      data: contralateralLabels.map((label, index) => {
        return { key: label, value: contralateralData[index] };
      }),
      parsing: {
        xAxisKey: "key",
        yAxisKey: "value",
      },
      fill: true,
      ...getGraphTypeColors(graphType, true),
      pointRadius: 6,
    });
  }

  const getOrCreateTooltip = (chart: any) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.style.background = "#fff"; // Set white background color
      tooltipEl.style.border = "1px solid #ccc";
      tooltipEl.style.borderRadius = "3px";
      tooltipEl.style.color = "#333"; // Set text color
      tooltipEl.style.opacity = "1";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";
      tooltipEl.style.padding = "6px 8px"; // Add padding

      const table = document.createElement("table");
      table.style.margin = "0px";

      tooltipEl.appendChild(table);
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  const handleBodyLines = (
    bodyLines: any,
    tooltip: any,
    td: any,
    tr: any,
    tooltipExamDetail: ExamItem,
    tableBody: any
  ) => {
    bodyLines.forEach((body: any, i: number) => {
      const colors = tooltip.labelColors[i];

      const span = document.createElement("span");
      span.style.background = colors.backgroundColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderWidth = "2px";
      span.style.marginRight = "10px";
      span.style.height = "10px";
      span.style.width = "10px";
      span.style.display = "inline-block";

      const clonedTd = td.cloneNode();
      const clonedTr = tr.cloneNode();
      const tooltipDataIndex = tooltip?.dataPoints?.[0]?.dataIndex;
      const titleText = document.createElement("span");
      titleText.style.color = "#5C5D68";
      titleText.style.fontSize = "12px";
      titleText.style.fontWeight = "400";
      const labelList: any =
        tooltip?.dataPoints?.[0]?.dataset.label === "Data"
          ? labels
          : contralateralLabels;
      titleText.textContent = moment(labelList[tooltipDataIndex || 0]).format(
        "MMM Do, YYYY"
      );

      const detailText = document.createElement("a");
      detailText.style.color = "#54B9EA";
      detailText.style.fontSize = "12px";
      detailText.style.fontWeight = "400";
      detailText.style.cursor = "pointer";
      detailText.textContent = "Details";
      detailText.classList.add("clickable"); // Add "clickable" class
      detailText.addEventListener("click", (event) => {
        if (tooltipExamDetail != undefined) {
          event.stopPropagation();
          window.open(
            `${getRouteLink(PAGES.videoReview)}${tooltipExamDetail?._id}/${
              tooltipExamDetail?.joint?._id
            }/${tooltipExamDetail?.patientId}/${tooltipExamDetail?.joint?.side}`
          );
        }
      });

      const flexContainer = document.createElement("div");
      flexContainer.style.display = "flex";
      flexContainer.style.justifyContent = "space-between";

      clonedTd.appendChild(flexContainer);
      flexContainer.appendChild(titleText);
      flexContainer.appendChild(detailText);
      clonedTr.appendChild(clonedTd);
      tableBody.appendChild(clonedTr);
    });
  };

  const handleTooltipBody = (
    tooltip: any,
    tooltipExamDetail: ExamItem,
    tooltipEl: any
  ) => {
    const bodyLines = tooltip.body.map((b: any) => b.lines);

    const tableHead = document.createElement("thead");
    const tableBody = document.createElement("tbody");
    const tr = document.createElement("tr");
    const td = document.createElement("td");

    handleBodyLines(bodyLines, tooltip, td, tr, tooltipExamDetail, tableBody);

    const movementText = document.createElement("span");
    movementText.style.color = "#161728";
    movementText.style.fontSize = "16px";
    movementText.style.fontWeight = "700";
    movementText.textContent = tooltipExamDetail?.movement?.name ?? "";

    const clonedTd = td.cloneNode();
    const clonedTr = tr.cloneNode();

    clonedTd.appendChild(movementText);

    clonedTr.appendChild(clonedTd);
    tableBody.appendChild(clonedTr);

    let min = 0;
    let max = 0;
    let range = max - min;

    if (graphType === LineType.Extension) {
      min = Number.parseFloat(
        data?.[tooltip?.dataPoints?.[0]?.dataIndex || 0]?.toFixed(2)
      );
      max = Number.parseFloat(
        opositeChartData[tooltip?.dataPoints?.[0]?.dataIndex || 0]?.toFixed(2)
      );
    } else {
      min = Number.parseFloat(
        opositeChartData[tooltip?.dataPoints?.[0]?.dataIndex || 0]?.toFixed(2)
      );
      max = Number.parseFloat(
        data?.[tooltip?.dataPoints?.[0]?.dataIndex || 0]?.toFixed(2)
      );
    }

    if (isNaN(max)) {
      max = 0;
    }

    if (isNaN(min)) {
      min = 0;
    }
    range = Number.parseFloat((max - min).toFixed(2));

    const minValueText = document.createElement("span");
    minValueText.style.color = "#5C5D68";
    minValueText.style.fontSize = "14px";
    minValueText.textContent = `Min: ${min}`;

    const maxValueText = document.createElement("span");
    maxValueText.style.color = "#5C5D68";
    maxValueText.style.fontSize = "14px";
    maxValueText.textContent = `Max: ${max}`;

    const rangeValueText = document.createElement("span");
    rangeValueText.style.color = "#5C5D68";
    rangeValueText.style.fontSize = "14px";
    rangeValueText.textContent = `Range: ${range}`;

    const clonedTd2 = td.cloneNode();
    const clonedTr2 = tr.cloneNode();

    clonedTd2.appendChild(minValueText);
    clonedTd2.appendChild(document.createTextNode(" "));
    clonedTd2.appendChild(maxValueText);
    clonedTd2.appendChild(document.createTextNode(" "));
    clonedTd2.appendChild(rangeValueText);

    clonedTr2.appendChild(clonedTd2);
    tableBody.appendChild(clonedTr2);

    const tableRoot = tooltipEl.querySelector("table");

    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  };

  const externalTooltipHandler = (context: any) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);
    let tooltipExamDetail = examList.filter((e: any) => {
      return (
        e.patientRegionId === ids[tooltip?.dataPoints?.[0]?.dataIndex ?? 0]
      );
    })[0];
    // Set Text
    if (tooltip.body) {
      handleTooltipBody(tooltip, tooltipExamDetail, tooltipEl);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
    tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
    tooltipEl.style.zIndex = 100;

    const hideTooltip = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!tooltipEl.contains(target)) {
        tooltipEl.style.opacity = "0";
        document.removeEventListener("click", hideTooltip);
      }
    };

    document.addEventListener("click", hideTooltip);
  };
  const options: ChartOptions<"line"> = {
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: {
            millisecond: "MM/DD/YY",
            second: "MM/DD/YY",
            minute: "MM/DD/YY",
            hour: "MM/DD/YY",
            day: "MM/DD/YY",
            week: "MM/DD/YY",
            month: "MM/DD/YY",
            quarter: "MM/DD/YY",
            year: "MM/DD/YY",
          },
        },
      },
    },
    plugins: {
      tooltip: {
        // Disable the on-canvas tooltip
        enabled: false,
        position: "nearest",
        external: externalTooltipHandler,
      },
      legend: {
        display: false, // Display legend for multiple lines
      },
      zoom: {
        pan: {
          enabled: false,
          mode: "xy",
        },
        zoom: {
          pinch: {
            enabled: false, // Enable pinch zooming
          },
          wheel: {
            enabled: false, // Enable wheel zooming
          },
          mode: "x",
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: true,
    },
    backgroundColor: "#F2F8FD",
    maintainAspectRatio: false,
  };

  return (
    <Grid
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        paddingX: 1,
      }}
    >
      <ModalImage
        icon={icons.information}
        graphType={graphType}
        content={LineTypeContent[graphType]}
      />
      <Grid
        sx={{
          backgroundColor: "#FAFAFA",
          padding: 1,
          borderRadius: 2,
          height: "100%",
        }}
      >
        <Line data={chartData} options={options} />
      </Grid>
      {checkIfAllHaveSameDate() && (
        <Typography>
          All exams on graph were performed{" "}
          {moment(new Date(checkIfAllHaveSameDate())).format("MM/DD/YY")}
        </Typography>
      )}
    </Grid>
  );
};

export default LineRegionChart;
